import { formatDeliverySchedule, type DeliveryEntry } from "./order-delivery-schedule";
import type { InsertOrder, Order } from "../platform/schema";
import { formatRawDecimal } from "../values/number-format";
import { decimalEqual, computedDecimalSchema } from "../values/decimal";

import { ORDER_STATUSES } from "./order-types";
import { ORDER_AMENDMENT_FIELD_LABELS } from "./order-ledger-copy";
export function canAmendOrderStatus(status: Order["orderStatus"]): boolean {
  return status === ORDER_STATUSES.requested || status === ORDER_STATUSES.ordered || status === ORDER_STATUSES.fulfilled;
}

export function canCancelOrderStatus(status: Order["orderStatus"]): boolean {
  return status === ORDER_STATUSES.requested || status === ORDER_STATUSES.ordered;
}

export const ORDER_AMENDABLE_FIELDS = [
  "direction",
  "contactId",
  "chemicalName",
  "casNumber",
  "quantity",
  "unit",
  "pricePerItem",
  "internalProductNumber",
  "externalProductNumber",
  "referenceNumber",
  "customerPoNumber",
  "contactAddressId",
  "requestedBy",
  "customerRequestorContactPersonId",
  "deliverySchedule",
  "notes",
] as const satisfies readonly (keyof InsertOrder)[];

export type OrderAmendableField = (typeof ORDER_AMENDABLE_FIELDS)[number];
export type OrderAmendmentPatch = Partial<Pick<InsertOrder, OrderAmendableField>>;
export type OrderAmendmentDisplayValues = Partial<Record<
  OrderAmendableField,
  { before: unknown; after: unknown }
>>;

export const ORDER_AMENDMENT_LABELS: Record<OrderAmendableField, string> = ORDER_AMENDMENT_FIELD_LABELS;

function historyValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function decimalHistoryValue(value: unknown): string {
  const parsed = computedDecimalSchema.safeParse(value);
  return parsed.success ? formatRawDecimal(parsed.data) : historyValue(value);
}

function equalDecimalHistoryValues(before: unknown, after: unknown): boolean {
  const left = computedDecimalSchema.safeParse(before);
  const right = computedDecimalSchema.safeParse(after);
  return left.success && right.success && decimalEqual(left.data, right.data);
}

function scheduleHistoryValue(value: unknown): string {
  if (typeof value === "string") return value;
  return formatDeliverySchedule((value ?? []) as DeliveryEntry[]) ?? historyValue(null);
}

type HistoryPolicy = { format: (value: unknown) => string; equal?: (before: unknown, after: unknown) => boolean };
const DECIMAL_HISTORY_POLICY: HistoryPolicy = { format: decimalHistoryValue, equal: equalDecimalHistoryValues };
const HISTORY_POLICIES: Partial<Record<OrderAmendableField, HistoryPolicy>> = {
  pricePerItem: DECIMAL_HISTORY_POLICY,
  quantity: DECIMAL_HISTORY_POLICY,
  deliverySchedule: { format: scheduleHistoryValue },
};

type AmendmentContext = { existing: Order; patch: OrderAmendmentPatch; displayValues: OrderAmendmentDisplayValues };

function amendmentField({ existing, patch, displayValues }: AmendmentContext, field: OrderAmendableField) {
  if (!(field in patch)) return [];
  const display = displayValues[field];
  const before = display?.before ?? existing[field];
  const after = display?.after ?? patch[field];
  const policy = HISTORY_POLICIES[field];
  if (policy?.equal?.(before, after)) return [];
  const format = policy?.format ?? historyValue;
  const beforeText = format(before);
  const afterText = format(after);
  return beforeText === afterText ? [] : [{ label: ORDER_AMENDMENT_LABELS[field], value: `${beforeText} → ${afterText}` }];
}

export function buildOrderAmendmentHistoryFields(
  existing: Order,
  patch: OrderAmendmentPatch,
  displayValues: OrderAmendmentDisplayValues = {},
) {
  return ORDER_AMENDABLE_FIELDS.flatMap(amendmentField.bind(null, { existing, patch, displayValues }));
}
